import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import type { Contact } from '../../types/contact';
import ProfileAvatar from '../common/ProfileAvatar';
import { Link, useNavigate } from 'react-router-dom';
import RequestQuoteModal from '../jobs/RequestQuoteModal';
import { useState } from 'react';
import { FreelancerProfile } from '../../types/profile';
import RatingStars from '../common/RatingStars';
import { Mail, MessageSquare, Phone } from 'lucide-react';
import { useProfileRole } from '../../hooks/useProfileRole';

import { languageOptions } from '../../utils/languagesUtils';
import FreelancerProfileModalContent from '../freelancers/FreelancerProfileModalContent';
import ProfileBioText from '../common/ProfileBioText';
import { FreelancerSearchResult } from '../../types/search';
import RecruiterProfileModalContent from '../profile/RecruiterProfileModalContent';
import ReportModal from '../reports/ReportModal';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import UpdateProfileStatusModal from '../UpdateProfileStatusModal';

interface ContactModalProps {
  contact: Contact;
  onClose: () => void;
}

export default function ContactModal({ contact, onClose }: ContactModalProps) {
  const { t } = useTranslation(['freelancers', 'common']);
  const [isQuoteRequestModalOpen, setIsQuoteRequestModalOpen] = useState(false);
  const { isRecruiter } = useProfileRole();

  const [showReportModal, setShowReportModal] = useState(false);

  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);

  const CloseButton = () => (
    <div>
      <button
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl md:top-1 md:right-4"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>
    </div>
  );

  const navigate = useNavigate();
  const allianceCount =
    isRecruiter && contact.alliance_count
      ? contact.alliance_count > 10
        ? '10+'
        : contact.alliance_count
      : null;

  const handleAction = () => {
    if (contact.contact_type === 'freelancer') {
      setIsQuoteRequestModalOpen(true);
    } else {
      navigate(`/job-search?userId=${contact.id}/`);
    }
  };

  const Divider = () => <hr className="my-6 border-[#E1E9F0]" />;

  const [copied, setCopied] = useState(false);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { isAdmin } = useProfileRole();

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[50rem]">
        <div className="flex flex-row items-center">
          <div className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:right-8 md:top-3.5">
            <ReportButtonMenu
              onReport={() => setShowReportModal(true)}
              entityOwner={contact.id}
              onCopySuccess={handleCopySuccess}
              contentTypeTitle="report_this_user"
            />
          </div>
          <CloseButton />
        </div>

        {/* version desktop */}
        {isRecruiter ? (
          <FreelancerProfileModalContent
            freelancer={contact as unknown as FreelancerSearchResult}
            className="pt-6 md:pt-0"
          />
        ) : (
          <RecruiterProfileModalContent contact={contact} className="pt-6 md:pt-0" />
        )}

        {/* version mobile */}
        <div className="flex flex-col items-center gap-2 md:hidden">
          {/* Avatar centré */}
          <ProfileAvatar
            profile_id={contact.id}
            first_name={contact.first_name ?? ''}
            avatar_url={contact.avatar_url}
            className="w-20 h-20"
          />

          <h2 className="text-lg font-bold text-secondary text-center m-0 p-0">
            {`${contact.first_name} ${contact.last_name}`}
          </h2>
          <div className="m-0 p-0">
            <div className="m-0 p-0">
              <span className="text-sm text-gray-500 m-0 p-0">{contact.job_title}</span>
            </div>
            <div className="m-0 p-0">
              <span className="text-sm text-gray-500 m-0 p-0">{contact.company_name}</span>
            </div>
          </div>

          <div className="w-full px-4 mt-2">
            <h5 className="text-[0.7rem] font-bold text-gray-500 mb-1">
              {t('businessIndustries', { defaultValue: "Secteurs d'activité" })}
            </h5>
            <p className="text-[0.6rem] flex flex-wrap gap-1">
              {isRecruiter
                ? contact.services?.map(ind => <span key={ind.id}>{ind.label}</span>)
                : contact.industries?.map(ind => <span key={ind.id}>{ind.label}</span>)}
            </p>
          </div>
          {isRecruiter ? (
            <div className="flex w-full justify-between px-4 ">
              <div className="flex flex-col items-start w-1/2">
                <h5 className="text-[0.7rem] font-bold text-gray-500 text-left">
                  {t('distance', { defaultValue: 'Distance' })}
                </h5>
                <p className="text-[0.6rem] mt-1 text-left">0km</p>
              </div>
              <div className="flex flex-col items-start w-1/2">
                <h5 className="text-[0.7rem] font-bold text-gray-500 text-left">
                  {t('availability', { defaultValue: 'Évaluation' })}
                </h5>
                <div className="mt-1">
                  <RatingStars
                    rating={contact.stats?.overall_rating ?? 0}
                    starClassName={'w-3 h-3'}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex w-full justify-between px-4">
              <div className="flex flex-col items-start w-1/2">
                <h5 className="text-[0.7rem] font-bold text-gray-500 text-left">
                  {t('location', { defaultValue: 'Localisation' })}
                </h5>
                <p className="text-[0.6rem] mt-1 text-left">{contact?.cityname}</p>
              </div>
              <div className="flex flex-col items-start w-1/2">
                <h5 className="text-[0.7rem] font-bold text-gray-500 text-left">
                  {t('distance', { defaultValue: 'Distance' })}
                </h5>
                <p className="text-[0.6rem] mt-1 text-left">0km</p>
              </div>
            </div>
          )}

          {isRecruiter ? (
            <div className="flex flex-col gap-1 items-start w-full px-4">
              <span className="text-sm">{t('alliances', { ns: 'freelancers' })}</span>
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <span className="text-white text-xs">{allianceCount}</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start w-full px-4">
              <h5 className="text-[0.7rem] font-bold text-gray-500 text-left">
                {t('availability', { defaultValue: 'Évaluation' })}
              </h5>
              <div className="mt-1">
                <RatingStars
                  rating={contact.stats?.overall_rating ?? 0}
                  starClassName={'w-3 h-3'}
                />
              </div>
            </div>
          )}
        </div>

        <Divider />
        <div className="grid grid-cols-1 md:grid-cols-[80%_20%] gap-4 md:gap-6 items-start">
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-secondary">
              {t('about', { ns: 'common', defaultValue: 'À propos' })}{' '}
            </h2>
            <div>
              {contact.bio ? (
                <ProfileBioText
                  bio={contact.bio}
                  profileId={contact.id}
                  className="text-secondary"
                  skipRedaction={true}
                />
              ) : (
                <p className="text-gray-500">
                  {t('noBio', { defaultValue: 'Aucune description disponible' })}
                </p>
              )}
            </div>
          </div>

          {isRecruiter && (
            <div className="flex md:justify-center items-start">
              <Link to={`/freelancers/${contact.id}`}>
                <Button
                  variant="transparent-with-border"
                  className="inline-flex !text-base md:!text-sm px-6 py-3 md:px-4 md:py-2 rounded-full w-full md:w-auto justify-center"
                >
                  {t('viewProfile', { ns: 'freelancers' })}
                </Button>
              </Link>
            </div>
          )}
        </div>
        <Divider />
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-secondary">
            {t('information', { ns: 'common', defaultValue: 'Informations' })}
          </h2>
          <div className="flex flex-col  gap-4 text-secondary text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-600" />
              <div>{contact?.email}</div>
            </div>

            {contact?.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-600" />
                <div>{contact?.phone}</div>
              </div>
            )}

            {contact?.languages && contact.languages.length > 0 && (
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-600" />
                <div>
                  {contact?.languages && contact.languages.length > 0
                    ? contact.languages
                        .map(
                          code => languageOptions.find(lang => lang.code === code)?.label || code
                        )
                        .join(', ')
                    : ''}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          {isAdmin ? (
            <Button
              variant="primary"
              onClick={() => setShowUpdateStatusModal(true)}
              className="w-full bg-primary text-white  mb-4 px-8 self-center md:w-auto md:justify-self-end md:self-end md:mb-0"
            >
              {t('update_status_button', { ns: 'common', defaultValue: 'Modifier le statut' })}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleAction}
              className="w-full bg-primary text-white  mb-4 px-8 self-center md:w-auto md:justify-self-end md:self-end md:mb-0"
            >
              {contact.contact_type === 'freelancer'
                ? t('requestQuote', { ns: 'common', defaultValue: 'Demander une soumission' })
                : t('viewJobs', { ns: 'common', defaultValue: "Voir les offres d'emploi" })}
            </Button>
          )}
        </div>

        {isQuoteRequestModalOpen && (
          <RequestQuoteModal
            onClose={() => setIsQuoteRequestModalOpen(false)}
            targetedFreelancer={contact as unknown as FreelancerProfile}
          />
        )}
      </FullBlockingModal>

      {showUpdateStatusModal && (
        <UpdateProfileStatusModal
          user={contact as unknown as FreelancerProfile}
          onClose={() => setShowUpdateStatusModal(false)}
        />
      )}

      {showReportModal && contact && (
        <ReportModal
          entityId={contact.id}
          reportedId={contact.id}
          contentType="profile"
          onClose={() => setShowReportModal(false)}
        />
      )}

      {copied && <BottomSuccessMessage />}
    </>
  );
}
